/**
 * PRESENCIA LIVE — LED de dispositivos en tiempo real para el árbol del admin.
 *
 * Por qué existe: el árbol del admin es una FOTO (se carga al entrar / tras una
 * acción CRUD, sin polling). El campo `online` que trae /api/admin/nodos/arbol es
 * correcto al momento de cargar (sale de la presencia en vivo del Go, SSOT con el
 * listado de dispositivos), pero no "respira": un aparato que cae no apaga su LED
 * hasta recargar. Este módulo abre UNA conexión SSE al broadcaster Go y repinta
 * SOLO el LED del serial afectado cuando llega una transición de presencia, sin
 * re-armar el árbol (cero DOM pesado).
 *
 * Diseño / garantías:
 *  - Suscribe SOLO al canal público `admin:presence` (lleva únicamente el serial:
 *    sin teléfono, sin saldo, sin nada sensible). NUNCA pide canales `user:*` —
 *    el Go los rechaza por device anchoring de todos modos, pero ni los pedimos.
 *  - 1 serial = 1 estado: el Go ya emite por TRANSICIÓN de presencia del
 *    dispositivo (conectado en 0→1, desconectado en 1→0), no por conexión/canal,
 *    así que add/delete directo es seguro y no duplica.
 *  - Foto inicial: la trae el árbol ya cargado (cada dispositivo con su `online`).
 *    Este módulo solo aplica los CAMBIOS encima — no necesita pedir /stats.
 *  - Reconexión: delegada al BroadcastClient SSOT (backoff eterno + reconexión por
 *    eventos online/visibilitychange del browser). El `tick` global alimenta su
 *    watchdog aunque el canal de presencia esté mudo.
 *  - Debounce anti-flapping: marcar OFFLINE espera ~2s (red flaky reconecta solo);
 *    marcar ONLINE es inmediato (no hay razón para demorar buenas noticias).
 *  - Defensa en profundidad: si por carga extrema el Go dropea un evento de
 *    presencia, la próxima recarga del árbol corrige (su `online` es la verdad de
 *    fondo). El stream es la capa de "vivo", no la fuente de verdad.
 */

import { BroadcastClient } from '../../../shared/services/broadcast-client.js';

const SSE_URL = import.meta.env.VITE_SSE_URL;
const CANAL_PRESENCIA_ADMIN = 'admin:presence';
const EVENTO_CONECTADO = 'dispositivo:conectado';
const EVENTO_DESCONECTADO = 'dispositivo:desconectado';
// Espera antes de aplicar OFFLINE: una reconexión flaky (1→0→1 en <2s) no debe
// parpadear el LED. ONLINE se aplica al instante.
const DEBOUNCE_OFFLINE_MS = 2000;

// serial → timer pendiente de marcar offline (para cancelar si reconecta antes).
const _timersOfflinePendientes = new Map();
let _conectado = false;

/**
 * Repinta el LED de un serial sin re-render del árbol. Busca el span del LED por
 * data-serial (puede haber >1 si el mismo serial aparece en varias vistas) y le
 * conmuta las clases online/offline. Si el serial no está en el DOM (árbol no
 * cargado, o nodo colapsado), no hace nada — al expandir/recargar se pinta bien.
 */
function _repintarLed(serial, online) {
    if (!serial) return;
    // El estado online/offline vive en el contenedor .dispositivo-leaf (CSS tiñe
    // ícono+nombre). Puede haber >1 match si el serial aparece en varias vistas.
    const sel = `.dispositivo-leaf[data-serial="${CSS.escape(serial)}"]`;
    const nodos = document.querySelectorAll(sel);
    for (let i = 0; i < nodos.length; i++) {
        nodos[i].classList.toggle('online', online);
        nodos[i].classList.toggle('offline', !online);
    }
}

function _marcarOnline(serial) {
    // Cancelar cualquier offline pendiente: reconectó antes del debounce.
    const t = _timersOfflinePendientes.get(serial);
    if (t) {
        clearTimeout(t);
        _timersOfflinePendientes.delete(serial);
    }
    _repintarLed(serial, true);
}

function _marcarOffline(serial) {
    // Debounce: si llega un conectado del mismo serial antes de DEBOUNCE_OFFLINE_MS,
    // _marcarOnline cancela este timer y el LED no parpadea.
    if (_timersOfflinePendientes.has(serial)) return;
    const timer = setTimeout(() => {
        _timersOfflinePendientes.delete(serial);
        _repintarLed(serial, false);
    }, DEBOUNCE_OFFLINE_MS);
    _timersOfflinePendientes.set(serial, timer);
}

export const PresenciaLive = {
    /**
     * Inicia la conexión SSE de presencia. Idempotente: llamarla más de una vez
     * no abre conexiones duplicadas (BroadcastClient es singleton y connect()
     * cierra la anterior).
     */
    init() {
        if (!SSE_URL) {
            // Sin SSE configurado (build viejo): el árbol sigue mostrando la foto
            // estática. Degradación silenciosa, no es error.
            return;
        }
        if (_conectado) return;
        _conectado = true;

        // Handlers PERSISTENTES (sobreviven a las reconexiones del BroadcastClient).
        BroadcastClient.on(EVENTO_CONECTADO, data => data?.serial && _marcarOnline(data.serial), true);
        BroadcastClient.on(EVENTO_DESCONECTADO, data => data?.serial && _marcarOffline(data.serial), true);

        // serial sintético del admin (no es un aparato): identifica esta conexión
        // en el Go sin colisionar con TVs/POS. channel = SOLO presencia (público).
        // privateChannel queda null → NUNCA pedimos user:* (cero acceso a datos
        // privados de jugadores). El tick global llega igual y alimenta el watchdog.
        BroadcastClient.connect({
            serverUrl: SSE_URL,
            clientId: 'admin-presence-' + Math.random().toString(36).slice(2, 10),
            channel: CANAL_PRESENCIA_ADMIN,
            privateChannel: null,
        });
    },

    /** Corta la conexión y limpia timers. Útil al cerrar sesión / salir del admin. */
    stop() {
        for (const t of _timersOfflinePendientes.values()) clearTimeout(t);
        _timersOfflinePendientes.clear();
        BroadcastClient.off(EVENTO_CONECTADO);
        BroadcastClient.off(EVENTO_DESCONECTADO);
        BroadcastClient.disconnect();
        _conectado = false;
    },
};

export default PresenciaLive;
